import { useCallback, useMemo, useReducer } from 'react';

import type { AuthConfig } from 'model/authConfig';

/**
 * Tiny reducer-backed form-state hook used by `AuthConfigManager`. Replaces
 * four independent `useState` calls so the four input fields can be set,
 * cleared, or hydrated from an existing `AuthConfig` through a single
 * stable surface.
 *
 * Surface intentionally narrow: only the operations the manager component
 * actually performs (per-field edits, reset to empty / preset, full
 * hydrate from a saved config). No async / no validation here — that
 * stays in the component.
 */
export interface AuthConfigFormState {
  name: string;
  apiBaseUrl: string;
  clientId: string;
  clientSecret: string;
}

type Action =
  | { type: 'setField'; field: keyof AuthConfigFormState; value: string }
  | { type: 'reset'; value?: AuthConfigFormState }
  | { type: 'loadFromConfig'; config: AuthConfig };

const EMPTY: AuthConfigFormState = {
  name: '',
  apiBaseUrl: '',
  clientId: '',
  clientSecret: '',
};

const reducer = (state: AuthConfigFormState, action: Action): AuthConfigFormState => {
  switch (action.type) {
    case 'setField':
      return { ...state, [action.field]: action.value };
    case 'reset':
      return action.value ?? EMPTY;
    case 'loadFromConfig':
      return {
        name: action.config.name,
        apiBaseUrl: action.config.apiBaseUrl,
        clientId: action.config.clientId,
        clientSecret: action.config.clientSecret,
      };
    default:
      return state;
  }
};

export interface UseAuthConfigFormResult {
  /** Current field values. */
  state: AuthConfigFormState;
  /** Update one field; other fields are preserved. */
  setField: (field: keyof AuthConfigFormState, value: string) => void;
  /** Clear all fields, or restore to a provided snapshot. */
  reset: (value?: AuthConfigFormState) => void;
  /** Populate every field from a saved `AuthConfig`. */
  loadFromConfig: (config: AuthConfig) => void;
}

export const useAuthConfigForm = (initial?: AuthConfigFormState): UseAuthConfigFormResult => {
  const [state, dispatch] = useReducer(reducer, initial ?? EMPTY);

  const setField = useCallback((field: keyof AuthConfigFormState, value: string) => {
    dispatch({ type: 'setField', field, value });
  }, []);

  const reset = useCallback((value?: AuthConfigFormState) => {
    dispatch({ type: 'reset', value });
  }, []);

  const loadFromConfig = useCallback((config: AuthConfig) => {
    dispatch({ type: 'loadFromConfig', config });
  }, []);

  return useMemo(
    () => ({ state, setField, reset, loadFromConfig }),
    [state, setField, reset, loadFromConfig],
  );
};
